import { _ } from "../../../polyglot/polyglot.js";
import { makeMonitorRepository } from "../../../repositories/repositoryBuilders.js";
import { router } from "@foretagsplatsen/widgetjs";
import AbstractImporter from "../AbstractImporter.js";
import AuthenticationStep from "../AuthenticationStep.js";
import ImportOptionsSelectionStep from "./ImportOptionsSelectionStep.js";
import QuickbooksIntegration from "../../../../models/integration/QuickbooksIntegration.js";
import session from "../../../session.js";

/**
 * `QuickbooksImporter` Import wizard for the Quickbooks integration.
 */
export default class QuickbooksImporter extends AbstractImporter {
	constructor() {
		super(...arguments);

		const antiForgeryToken = session.getAntiForgeryToken();

		this._quickbooksRepository = makeMonitorRepository({
			route: "/Shared/Import/Quickbooks",
			antiForgeryToken,
		});

		this._quickbooksFiscalYearsRepository = makeMonitorRepository({
			route: "/Shared/Import/Quickbooks/FiscalYears",
			antiForgeryToken,
		});

		this._companyChartOfAccountsRepository = makeMonitorRepository({
			route: "/Shared/Company/#companyId/ChartOfAccounts",
			antiForgeryToken,
		});

		this._budgetRepository = makeMonitorRepository({
			route: "/Accounting2/Company/#companyId/Budget/",
			antiForgeryToken,
		});

		this._router = router.getRouter();
	}

	routeName() {
		return "quickbooks";
	}

	hasIntegration() {
		return this._context.company.hasQuickbooksIntegration();
	}

	_getCompanyId() {
		const company = this._context.company;

		if (company.isNullCompany()) return null;

		return company.getLegacyId();
	}

	_wizardSteps() {
		return [
			this._createAuthenticationStep(),
			this._createImportOptionsSelectionStep(),
		];
	}

	// Step 1: Launch the authentication flow.
	_createAuthenticationStep() {
		return new AuthenticationStep({
			submitCallback: this._launchAuthenticationFlow.bind(this),
		});
	}

	// Step 2: Ask the user to select which fiscalYear to import.
	_createImportOptionsSelectionStep() {
		return new ImportOptionsSelectionStep({
			submitCallback: this._fiscalYearToImportData.bind(this),
		});
	}

	// Step 1 => 2: Authenticate the user if needed or proceed with fetching
	// the fiscal years.
	_launchAuthenticationFlow({ onDone, step }) {
		this._agencyId =
			this._context.agency?.getId() ||
			this._context.company?.getAgencyId();

		this._companyId =
			this._router.getParameter("companyId") || this._getCompanyId();

		const hasAccessToken =
			this._router.getParameter("hasAccessToken") === "true";

		this._context.importAttachments =
			this._context.company.getQuickbooksIntegrationAttachmentImportOption();

		if (hasAccessToken) {
			// User is authenticated, proceed with the flow.
			this._authenticationToFiscalYears({ onDone, step });
		} else {
			// New integration, redirect to QuickBooks for authentication.
			this._redirectToQuickBooks();
		}
	}

	// Step 1 ⇒ Step 2: get the list of available fiscal years
	// associated with the authentication.
	_authenticationToFiscalYears({ onDone, step }) {
		this._quickbooksFiscalYearsRepository
			.get({
				filter: { companyId: this._companyId },
			})
			.then((fiscalYears) => {
				if (fiscalYears.length === 0) {
					step.addSubmitError(_("No years found to import."));
				} else {
					this._context.fiscalYears = fiscalYears;
				}
				onDone();
			})
			.catch((error) => {
				step.addSubmitError(this._errorToMessage(error));
				onDone();
			});
	}

	// Step 2 ⇒ Step 3: get all the data corresponding to the selected
	// fiscal year (e.g., budgets, dimensions, dates, …)
	async _fiscalYearToImportData({ onDone, step }) {
		const [importData, companyChartsOfAccounts, budgets] =
			await Promise.all([
				this._fetchQuickbooksData(),
				this._fetchChartsOfAccounts(),
				this._fetchBudgets(),
			]).catch((error) => {
				step.addSubmitError(this._errorToMessage(error));
				onDone();
			});

		const allChartsOfAccounts = new Set([
			...(importData.chartOfAccounts || []),
			...companyChartsOfAccounts,
		]);

		const allBudgets = new Set([...(importData.budgets || []), ...budgets]);

		importData.chartOfAccounts = [...allChartsOfAccounts];
		importData.budgets = [...allBudgets];

		this._context.importData = importData;
		onDone();
	}

	// Step 1 => 2: Redirect to Quickbooks in order to authenticate with OAuth2.
	_redirectToQuickBooks() {
		let query = `${
			window.location.pathname
		}#!/import/${this.routeName()}?hasAccessToken=true`;

		// Return and abort url need to be set.
		// Otherwise, the default ones are lost after the redirection from Quickbooks,
		// resulting in the Cancel and Done buttons redirecting to broken urls.
		query += `&abortUrl=${this._router.getParameter("abortUrl")}`;
		query += `&returnUrl=${this._router.getParameter("returnUrl")}`;
		if (this._companyId) {
			query += `&companyId=${this._companyId}`;
		}

		const businessIdentityCode =
			this._context.company.getBusinessIdentityCode();

		// The business identity code is needed to retrieve the company after returning
		// from the authorization step.

		if (businessIdentityCode) {
			query += `&businessIdentityCode=${businessIdentityCode}`;
		}

		let redirectUrl = `/Shared/Import/QuickbooksOAuth2?agencyId=${
			this._agencyId
		}&returnUrl=${encodeURIComponent(query)}`;
		if (this._companyId) {
			redirectUrl += `&companyId=${this._companyId}`;
		}
		this._router.redirectToLocationPath(redirectUrl);
	}

	_fetchBudgets() {
		return this._budgetRepository.get({
			filter: { companyId: this._companyId },
		});
	}

	_fetchChartsOfAccounts() {
		return this._companyChartOfAccountsRepository.get({
			filter: { companyId: this._companyId },
		});
	}

	_fetchQuickbooksData() {
		return this._quickbooksRepository.save({
			filter: {
				companyId: this._companyId,
			},
			model: {
				fiscalYear: this._context.fiscalYear,
				importAttachments: this._context.importAttachments,
			},
			notify: false,
		});
	}

	_wantExtendedActionsInConfigureImportDataStep() {
		return true;
	}

	_errorToMessage(error) {
		return error.responseJSON.message;
	}
}
QuickbooksImporter.integrationType = QuickbooksIntegration.getType();
